import React, { useState } from "react";
import {
  Box,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import customDropzoneStyles from "./CustomDropzone.styles";
import { openErrorNotification } from "helpers/methods";

interface CustomProps {
  sx?: any; //styles for the outer border box
  innerBox?: any; //styles for inner main box
  dropzoneText?: string;
  dropzoneTextStyles?: any;
  icon?: any;
  onAdd: Function;
  maxFileSize: number;
  acceptedFiles: string[];
  showAlerts: boolean;
  fileNames?: string[]; //You can keep state in parent component that keeps storing names of files you upload.
  uniqueId?: string | number; //required in case you are rendering more than one Dropzone in one component.
  multipleFileUpload: boolean;
  disabled?: boolean;
  innerBody?: JSX.Element;
  fileExtenstionText?: string;
}

const CustomDropzone = (props?: CustomProps) => {
  const classes = customDropzoneStyles;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isDragOver, setIsDragOver] = useState(false);
  const fileSizeAccepted = props?.maxFileSize
    ? props.maxFileSize
    : 10 * 1024 * 1024; // 10MB in bytes
  const appliedClass = props?.sx
    ? { ...props.sx, ...classes.dropzone }
    : classes.dropzone;
  const disabled = props?.disabled ?? false;
  const fileSizeInMB = fileSizeAccepted / (1024 * 1024);

  const checkFileSizeAndFormat = (file: File): boolean => {
    if (file.size > fileSizeAccepted) {
      return false;
    }

    const fileExtension = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();
    if (!props?.acceptedFiles.includes(fileExtension)) {
      return false;
    }

    if (props.fileNames && props.fileNames.includes(file.name)) {
      openErrorNotification(
        `File ${file.name} already exists. Please try renaming your file.`
      );
      return false;
    }

    return true;
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer?.files;
    if (files) {
      props?.onAdd(files);
    }
  };

  // const handleFiles = (files: any) => {
  //   for (const file of files) {
  //     const reader = new FileReader();

  //     reader.onload = (event) => {
  //       if (event.target && typeof event.target.result === "string") {
  //         const image = new Image();
  //         image.src = event.target.result;
  //         document.body.appendChild(image);
  //       }
  //     };

  //     reader.readAsDataURL(file);
  //   }
  // };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const validFiles: File[] = [];
      const invalidFiles: string[] = [];
      const fileSizeExceededFiles: string[] = [];

      Array.from(files).forEach((file) => {
        if (checkFileSizeAndFormat(file)) {
          validFiles.push(file);
        } else {
          if (file.size > fileSizeAccepted) {
            fileSizeExceededFiles.push(file.name);
          } else {
            invalidFiles.push(file.name);
          }
        }
      });

      let errorMessage = "";
      if (invalidFiles.length > 0) {
        errorMessage += `File(s) [${invalidFiles.join(
          ", "
        )}] have an unsupported file format.\n`;
      }

      if (fileSizeExceededFiles.length > 0) {
        errorMessage += `File(s) [${fileSizeExceededFiles.join(
          ", "
        )}] exceeds the maximum allowed file size.`;
      }

      if (errorMessage) {
        openErrorNotification(errorMessage);
      }

      if (validFiles.length > 0) {
        props?.onAdd(validFiles);
      }
    }
  };

  return (
    <>
      <input
        id={`drag&drop${props?.uniqueId}`}
        style={{ display: "none" }}
        type="file"
        onChange={handleFileInputChange}
        value={""}
        multiple={props?.multipleFileUpload}
        accept={props?.acceptedFiles.join(",")}
        disabled={disabled}
      />
      <label htmlFor={`drag&drop${props?.uniqueId}`} style={{ width: "100%" }}>
        <Box
          sx={
            isDragOver ? { ...appliedClass, ...classes.dragover } : appliedClass
          }
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {props?.innerBody ? (
            props?.innerBody
          ) : (
            <Box sx={{ ...classes.greyBox, ...props?.innerBox }}>
              <Grid
                container
                direction={isMobile ? "column" : "row"}
                spacing={1}
                justifyContent="center"
                alignItems="center"
              >
                <Stack
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  spacing={2}
                  p={1.5}
                >
                  <img src={props?.icon} width={isMobile ? "20px" : "45px"} />
                  <Box px={1} pt={1}>
                    <Typography
                      fontSize={isMobile ? "0.8rem" : "1.25rem"}
                      sx={
                        props?.dropzoneTextStyles
                          ? props.dropzoneTextStyles
                          : classes.dropzoneText
                      }
                      gutterBottom
                    >
                      {props?.dropzoneText}
                    </Typography>
                    <Typography
                      sx={classes.acceptedFilesText}
                      fontSize={isMobile ? "0.5rem" : "0.775rem"}
                    >
                      {props?.fileExtenstionText
                        ? props?.fileExtenstionText
                        : `${props?.acceptedFiles
                            ?.map((file) => file.replace(".", "").toUpperCase())
                            .join(", ")} format(s) up to ${fileSizeInMB} MB`}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            </Box>
          )}
        </Box>
      </label>
    </>
  );
};

export default CustomDropzone;
